
import React from "react";
import { i18nManager } from "../../i18n";
import * as ReactRouterDom from "react-router-dom";
import HeroImage from "../../assets/images/hero_image.png";
import { FeatureFlagEvent, useFeatureFlags } from "@syllektis/oya-gbewa-react";
import { CacheManager, Debugger, LzEncryptor, Schemes, Utils } from "../../utils";
import { Button, Classname, Dropdown, FormControl, Popover, PopoverManageRef, Scheme, TextInput } from "@ronuse/noseur";
import { ApiResponseData, IPlatform, IProfile, PlatformService, SanitizedResponse, SupportRequestService } from "../../services";

export type HeaderProps = {
    title?: string;
    heroic?: boolean;
    subTitle?: string;
    searching?: boolean;
    authorized?: boolean;
    onTokenRefreshed?: () => void;
    reportPlatform?: (p: IPlatform) => void;
};

function Header(props: HeaderProps) {
    const labels = i18nManager.Labels.header;
    const navigate = ReactRouterDom.useNavigate();
    const { platform } = ReactRouterDom.useParams();
    const platformService = PlatformService.getInstance();
    const settingButton = React.useRef<HTMLButtonElement>(null);
    const supportRequestService = SupportRequestService.getInstance();
    const profilePopOverManageRef = React.useRef<PopoverManageRef>(null);
    const profile = CacheManager.get(CacheManager.PROFILE_KEY) as IProfile;
    const [openTicketCount, setOpenTicketCount] = React.useState<number>();
    const [searching, setSearching] = React.useState<boolean>(props.searching ?? false);
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [searchQuery, setSearchQuery] = React.useState<string>(searchParams.get("q") ?? "");
    const [activePlatform, setActivePlatform] = React.useState<IPlatform | undefined>(CacheManager.getPlatform(platform));
    const { enableSupportResources, enableSupportCommunities, allowUserTicketManagement } = useFeatureFlags({
        registerEvents: [FeatureFlagEvent.CHANGE],
        query: { platform },
        flags: {
            enableSupportResources: { id: "show-support-resources", initialValue: false },
            enableSupportCommunities: { id: "show-support-communities", initialValue: false },
            allowUserTicketManagement: { id: "allow-user-ticket-management", initialValue: false },
        },
    });

    React.useEffect(() => {
        if (!searchParams.has("pas")) return;
        Utils.processSigninData(searchParams.get("pas")!, () => {
            searchParams.delete("pas");
            setSearchParams(searchParams);
        });
    }, []);

    React.useEffect(() => {
        if (!platform) {
            navigate("/");
            return;
        }
        function fetchOpenedSupportRequests(activePlatform: IPlatform) {
            const platformPrimaryColor = activePlatform.colors?.split(",")[0].trim();
            Utils.loadStyle(`
                body {
                    --platformColor: ${platformPrimaryColor};
                    --platformColorRgb: ${Utils.hexToRgb(platformPrimaryColor)};
                }  
            `);
            supportRequestService.countOpenedSupportTicket(activePlatform!.external_id).then(({ sanitized }: SanitizedResponse<any>) => {
                Debugger.log("Header:useEffect:countOpenedSupportTicket", sanitized);
                setOpenTicketCount(sanitized);
            }).catch((error: SanitizedResponse<any>) => {
                Debugger.error("Header:useEffect:countOpenedSupportTicket:error", error);
            });
        }
        if (activePlatform && (activePlatform.slug == platform || activePlatform.platform_id == platform)) {
            fetchOpenedSupportRequests(activePlatform);
            return;
        }
        platformService.getPlatform(platform).then(({ apiResponse: { data } }: SanitizedResponse<any>) => {
            Debugger.log("Header:useEffect:getPlatform", platform, data);
            setActivePlatform(data! as IPlatform);
            props.reportPlatform?.(data! as IPlatform);
            CacheManager.insecurePut(CacheManager.ACTIVE_PLATFORM, data!, sessionStorage);
            fetchOpenedSupportRequests(data as IPlatform);
        }).catch((error: SanitizedResponse<any>) => {
            Utils.reportError(error);
            navigate("/");
        });
    }, []);

    if (!activePlatform) {
        return Utils.LOADING_STATE;
    }

    return (<div className="header" style={{ backgroundImage: (`linear-gradient(to bottom right, ${activePlatform?.colors})`) }} ref={(r) => {
        if (!r) return;
        r.scrollIntoView(); // TODO use boolean state to see if already in view if the custom platform loader does not work
    }}>
        <div className="menubar">
            <a className="left" href={`/${platform}/`} >
                <img src={activePlatform.logo_url ?? i18nManager.Labels.logo} alt={`${activePlatform.name} Support`} />
                <div className="logo">{activePlatform.name} {i18nManager.Labels.support_t}</div>
            </a>
            <div className="right">
                {!props.heroic && !searching ? (<Button leftIcon="fa fa-search" scheme={Scheme.LIGHT} textOnly onClick={toggleSearch} />) : null}
                {!searching ? labels.menu_items.filter((menuItem) => {
                    if (menuItem.location.includes("resources")) return enableSupportResources;
                    if (menuItem.location.includes("communities")) return enableSupportCommunities;
                    return true;
                }).map((menuItem) => (<ReactRouterDom.NavLink key={menuItem.label} to={`/${platform}` + menuItem.location}>{menuItem.label}</ReactRouterDom.NavLink>)) : null}
                {searching ? (<FormControl leftContent={<Button leftIcon="fa fa-times" scheme={Schemes.RIVTN_HERA} style={{ margin: 3 }} onClick={toggleSearch} outlined />}
                    rightContent={<Button text={labels.search} scheme={Schemes.RIVTN_HERA} leftIcon="fa fa-search" style={{ margin: 3 }} onClick={search} />} fill>
                    <TextInput defaultValue={searchQuery} onInputComplete={setSearchQuery} />
                </FormControl>) : null}
                {(allowUserTicketManagement) && (<>
                    {Utils.buildProfileDropdown(platform!, profile, settingButton, profilePopOverManageRef, labels, openTicketCount)}
                    {!profile?.email ? <Button onClick={Utils.signIn} text={labels.login} scheme={Scheme.LIGHT} style={{ color: "black", padding: "10px 30px", marginRight: 10, minWidth: 50 }} /> : null}
                </>)}
            </div>
        </div>
        {props.title && (<div className="title">{props.title}<br />{props.subTitle && (<span className="sub">{props.subTitle}</span>)}</div>)}
        {props.heroic ? (<div className="hero">
            <div>
                <div className="center" style={{ marginBottom: "20%" }}>
                    <span className="t">{labels.hero_title}</span>
                    <span className="d">{labels.hero_desc}</span>
                    <FormControl
                        rightContent={<Button text={labels.search} scheme={Schemes.RIVTN_HERA} leftIcon="fa fa-search" style={{ margin: 3 }} onClick={search} />} fill>
                        <TextInput defaultValue={searchQuery} onInputComplete={setSearchQuery} />
                    </FormControl>
                </div>
                <div className="right">
                    <img alt="Rivtn Support" src={HeroImage} />
                </div>
            </div>
        </div>) : null}
    </div>);

    async function toggleSearch() {
        setSearching(!searching);
    }

    async function search() {
        navigate({
            pathname: `/${platform}/search`,
            search: `q=${searchQuery}`
        });
    }

}

export default Header;
